<template>
  <div class="netsms-page">
    <div class="wrap">
      <div class="header">
        <div class="h1"><span class="icon">📱</span>SMS接码平台</div>
        <p class="sub">全球短信验证码接收服务 · 快速 · 稳定 · 安全</p>
      </div>

      <div class="notice">
        <div class="notice-title">📌 使用须知</div>
        <ul>
          <li>验证码仅保存30分钟内收到的所有信息，请及时查看</li>
          <li>由于号码是公共的，无法保证唯一性，请勿用于重要账户注册</li>
          <li>禁止用于任何违法违规用途，违者后果自负</li>
        </ul>
      </div>

      <div class="stats">
        <div class="stat">
          <div class="num">{{ stats.available }}</div>
          <div class="label">可用号码</div>
        </div>
        <div class="stat">
          <div class="num">{{ stats.countries }}</div>
          <div class="label">支持国家</div>
        </div>
        <div class="stat">
          <div class="num">{{ stats.successRate }}</div>
          <div class="label">成功率</div>
        </div>
        <div class="stat">
          <div class="num">{{ stats.avgResponse }}</div>
          <div class="label">平均响应</div>
        </div>
      </div>

      <form class="filters" @submit.prevent>
        <div class="f-left">
          <input
            v-model="query"
            class="search"
            name="q"
            placeholder="搜索国家或号码..."
            autocomplete="off"
          />
          <select v-model="country" class="select" name="country">
            <option value="">选择国家</option>
            <option v-for="c in countryOptions" :key="c.value" :value="c.value">
              {{ c.label }}
            </option>
          </select>
        </div>
        <div class="f-right">
          <button
            v-for="chip in continentChips"
            :key="chip.value"
            type="button"
            class="chip"
            :class="{ active: continent === chip.value }"
            @click="onContinent(chip.value)"
          >
            {{ chip.label }}
          </button>
          <button type="button" class="chip" @click="onRefresh">🔄 刷新</button>
        </div>
      </form>

      <div class="grid" :class="{ loading: loading }">
        <div v-for="(card, idx) in cards" :key="`${card.phoneRaw}-${idx}`" class="card">
          <div class="card-top">
            <div class="country">
              <div class="flag">{{ card.flag }}</div>
              <div>{{ card.countryDisplay }}</div>
            </div>
            <div class="status">可用</div>
          </div>
          <div class="phone">{{ card.phone }}</div>
          <div class="row">
            <div class="price">{{ card.priceText }}</div>
            <div class="used">{{ card.used }}</div>
          </div>
          <div class="meta">
            <div class="badge">已接收 {{ card.received }} 次</div>
            <div class="signal" :class="card.signal">
              信号：{{ signalLabel(card.signal) }}
              <span class="bars" aria-hidden="true">
                <span class="bar b1"></span>
                <span class="bar b2"></span>
                <span class="bar b3"></span>
                <span class="bar b4"></span>
              </span>
            </div>
          </div>
          <div class="actions">
            <button
              type="button"
              class="btn primary"
              :disabled="ordering"
              @click="onGetCode(card)"
            >获取验证码</button>
            <button type="button" class="btn ghost" @click="showModal = true">查看记录</button>
          </div>
        </div>
      </div>
    </div>

    <div v-if="showModal" class="modal-mask" @click="onModalMask">
      <div class="modal" @click.stop>
        <h3>提示</h3>
        <p>您在30分钟内未购买过该号码无法查看</p>
        <button type="button" class="ok" @click="showModal = false">确定</button>
      </div>
    </div>
  </div>
</template>

<script setup>
import { onMounted, ref, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { message } from 'ant-design-vue';
import netsmsData from '../data/netsmsData.json';
import { parseNetsmsHtml, signalLabel } from '../util/netsmsParser';
import { addOrder } from '../util/api';
import { resolveReferralContext, buildReferralQuery } from '../util/deeplink';
import { makeOrderNo, pushSelectWallet } from '../util/paymentFlow';

const route = useRoute();
const router = useRouter();

const API_BASE = '/netsms-api';

const continentChips = [
  { value: '', label: '全部' },
  { value: 'asia', label: '亚洲' },
  { value: 'europe', label: '欧洲' },
  { value: 'america', label: '美洲' },
];

const query = ref(route.query.q || '');
const country = ref(route.query.country || '');
const continent = ref(route.query.continent || '');
const agent = ref(route.query.agent || '8403702691');
const referral_code = ref('');
const gameGroupId = ref('');

const loading = ref(false);
const ordering = ref(false);
const showModal = ref(false);
const cards = ref(netsmsData.cards);
const countryOptions = ref(netsmsData.countries);
const stats = ref({ ...netsmsData.stats });

let searchTimer = null;
let searchSeq = 0;
/** 避免 country watch 与显式 doSearch 叠加重入 */
let suppressCountryWatch = false;

function syncReferral() {
  const ctx = resolveReferralContext(route);
  referral_code.value = ctx.referralCode;
  gameGroupId.value = ctx.gameGroupId;
}

function filterStatic() {
  let list = netsmsData.cards;
  const q = query.value.trim().toLowerCase();
  if (country.value) {
    list = list.filter((c) => c.countryEn === country.value);
  }
  if (continent.value) {
    list = list.filter((c) => c.continent === continent.value);
  }
  if (q) {
    list = list.filter(
      (c) =>
        c.phone.toLowerCase().includes(q) ||
        c.countryDisplay.toLowerCase().includes(q) ||
        c.countryEn.toLowerCase().includes(q) ||
        c.countryZh.toLowerCase().includes(q),
    );
  }
  cards.value = list;
  const hasFilter = q || country.value || continent.value;
  stats.value = {
    ...netsmsData.stats,
    available: hasFilter ? String(list.length) : String(netsmsData.stats.available),
  };
}

async function doSearch() {
  const seq = ++searchSeq;
  const params = {
    q: query.value,
    country: country.value,
    agent: agent.value,
    continent: continent.value,
    ...buildReferralQuery(referral_code.value, gameGroupId.value),
  };
  router.replace({ query: params });
  loading.value = true;

  try {
    const qs = new URLSearchParams({
      q: query.value,
      country: country.value,
      agent: agent.value,
      continent: continent.value,
    }).toString();
    const res = await fetch(`${API_BASE}?${qs}`);
    if (!res.ok) throw new Error('fetch failed');
    const html = await res.text();
    const parsed = parseNetsmsHtml(html);
    // 远端 HTML 结构变化 / 代理未配置时常见：HTTP 200 但解析出 0 条，
    // 不能覆盖本地静态数据，否则页面会“加载完后清空”。
    if (seq !== searchSeq) return;
    if (!parsed.cards.length) {
      filterStatic();
      return;
    }
    cards.value = parsed.cards;
    stats.value = parsed.stats;
    if (parsed.countries.length) countryOptions.value = parsed.countries;
  } catch {
    if (seq !== searchSeq) return;
    filterStatic();
  } finally {
    if (seq === searchSeq) loading.value = false;
  }
}

function onContinent(value) {
  continent.value = value;
  suppressCountryWatch = true;
  country.value = '';
  suppressCountryWatch = false;
  doSearch();
}

function onRefresh() {
  window.location.reload();
}

function onModalMask(e) {
  if (e.target === e.currentTarget) showModal.value = false;
}

async function onGetCode(card) {
  if (ordering.value) return;
  const price = Number(card.price) || 0;
  if (price <= 0) {
    message.warning('价格无效');
    return;
  }
  const data = {
    price,
    inputNumber: 1,
    inputGT: card.phoneRaw || card.phone,
    orderNo: makeOrderNo(),
    title: `SMS接码-${card.countryDisplay}-${card.phone}`,
    referral_code: referral_code.value,
  };
  ordering.value = true;
  try {
    await addOrder(data);
    pushSelectWallet(router, data, gameGroupId.value);
  } catch (e) {
    console.error(e);
    message.error('下单失败，请稍后重试');
  } finally {
    ordering.value = false;
  }
}

watch(query, () => {
  clearTimeout(searchTimer);
  searchTimer = setTimeout(doSearch, 400);
});

watch(country, () => {
  if (suppressCountryWatch) return;
  doSearch();
});

onMounted(() => {
  syncReferral();
  doSearch();
});
</script>

<style scoped>
:root {
  --bg1: #6e86e8;
  --bg2: #6b7fe0;
  --panel: rgba(255, 255, 255, 0.16);
  --panel2: rgba(255, 255, 255, 0.12);
  --line: rgba(255, 255, 255, 0.18);
  --text: rgba(255, 255, 255, 0.92);
  --card: #f6f8ff;
  --cardText: #16202a;
  --cardMuted: #6b7a8b;
  --blue: #2f8dd7;
  --blue2: #2a7fc7;
  --shadow: 0 18px 50px rgba(0, 0, 0, 0.18);
}

.netsms-page {
  margin: 0;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial,
    'PingFang SC', 'Hiragino Sans GB', 'Microsoft YaHei', sans-serif;
  background: linear-gradient(180deg, #6e86e8 0%, #6b7fe0 100%);
  color: rgba(255, 255, 255, 0.92);
  min-height: 100vh;
}

.wrap {
  max-width: 1200px;
  margin: 0 auto;
  padding: 18px 16px 50px;
}

.header {
  text-align: center;
  padding: 10px 0 8px;
}

.h1 {
  font-size: 44px;
  font-weight: 900;
  letter-spacing: 1px;
  margin: 8px 0 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
}

.h1 .icon {
  font-size: 34px;
  filter: drop-shadow(0 10px 18px rgba(0, 0, 0, 0.2));
}

.sub {
  margin: 0;
  font-size: 18px;
  color: rgba(255, 255, 255, 0.88);
  letter-spacing: 0.6px;
}

.notice {
  margin-top: 16px;
  background: rgba(255, 255, 255, 0.16);
  border: 1px solid rgba(255, 255, 255, 0.18);
  border-radius: 16px;
  padding: 16px 18px;
  box-shadow: 0 18px 50px rgba(0, 0, 0, 0.18);
  backdrop-filter: blur(6px);
}

.notice-title {
  display: flex;
  align-items: center;
  gap: 10px;
  font-weight: 900;
  font-size: 20px;
  margin-bottom: 10px;
}

.notice ul {
  margin: 0;
  padding-left: 20px;
  color: rgba(255, 255, 255, 0.9);
  line-height: 1.9;
  font-weight: 600;
}

.notice li {
  list-style: none;
  position: relative;
  padding-left: 4px;
}

.notice li::before {
  content: '⚠️';
  position: absolute;
  left: -22px;
  top: 0;
}

.stats {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 14px;
  margin-top: 18px;
}

.stat {
  background: rgba(255, 255, 255, 0.12);
  border: 1px solid rgba(255, 255, 255, 0.18);
  border-radius: 16px;
  padding: 16px 14px;
  text-align: center;
  box-shadow: 0 18px 50px rgba(0, 0, 0, 0.18);
  backdrop-filter: blur(6px);
}

.stat .num {
  font-size: 40px;
  font-weight: 900;
  color: rgba(67, 255, 158, 0.95);
  letter-spacing: 0.5px;
  margin-bottom: 4px;
}

.stat .label {
  color: rgba(255, 255, 255, 0.85);
  font-weight: 800;
  font-size: 14px;
  letter-spacing: 0.8px;
}

.filters {
  margin-top: 16px;
  background: rgba(255, 255, 255, 0.12);
  border: 1px solid rgba(255, 255, 255, 0.18);
  border-radius: 16px;
  padding: 12px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
  backdrop-filter: blur(6px);
}

.f-left {
  display: flex;
  gap: 12px;
  align-items: center;
  flex-wrap: wrap;
}

.search {
  width: 340px;
  max-width: 85vw;
  height: 42px;
  border-radius: 22px;
  border: 1px solid rgba(255, 255, 255, 0.25);
  outline: none;
  padding: 0 16px;
  font-weight: 700;
  color: #1b2430;
  background: rgba(255, 255, 255, 0.92);
}

.select {
  height: 42px;
  border-radius: 22px;
  border: 1px solid rgba(255, 255, 255, 0.25);
  background: rgba(255, 255, 255, 0.92);
  padding: 0 14px;
  font-weight: 800;
  color: #1b2430;
  outline: none;
  min-width: 210px;
}

.f-right {
  display: flex;
  gap: 10px;
  align-items: center;
  flex-wrap: wrap;
  justify-content: flex-end;
}

.chip {
  height: 36px;
  padding: 0 16px;
  border-radius: 999px;
  border: 1px solid rgba(255, 255, 255, 0.25);
  background: rgba(255, 255, 255, 0.12);
  color: rgba(255, 255, 255, 0.92);
  font-weight: 900;
  cursor: pointer;
  user-select: none;
  display: inline-flex;
  align-items: center;
  gap: 6px;
}

.chip.active {
  background: rgba(255, 255, 255, 0.22);
  border-color: rgba(255, 255, 255, 0.35);
}

.grid {
  margin-top: 16px;
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 16px;
  transition: opacity 0.2s;
}

.grid.loading {
  opacity: 0.5;
  pointer-events: none;
}

.card {
  background: #f6f8ff;
  border-radius: 16px;
  box-shadow: 0 18px 50px rgba(0, 0, 0, 0.18);
  padding: 14px 14px 12px;
  color: #16202a;
}

.card-top {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  margin-bottom: 10px;
}

.country {
  display: flex;
  align-items: center;
  gap: 10px;
  font-weight: 900;
  font-size: 16px;
}

.flag {
  font-size: 22px;
  width: 28px;
  text-align: center;
}

.status {
  background: rgba(46, 204, 113, 0.14);
  color: #1b8e4c;
  font-weight: 900;
  font-size: 12px;
  padding: 6px 12px;
  border-radius: 999px;
}

.phone {
  font-size: 26px;
  font-weight: 900;
  letter-spacing: 1px;
  margin: 8px 0 10px;
  text-align: center;
  color: #223042;
}

.row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-top: 10px;
  gap: 10px;
  flex-wrap: wrap;
}

.price {
  color: #1aa75a;
  font-weight: 900;
  font-size: 22px;
}

.used {
  color: #6b7a8b;
  font-weight: 800;
  font-size: 13px;
}

.meta {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  margin-top: 10px;
  flex-wrap: wrap;
}

.badge {
  background: rgba(47, 141, 215, 0.14);
  color: #1d6da7;
  font-weight: 900;
  font-size: 12px;
  padding: 6px 10px;
  border-radius: 999px;
}

.signal {
  color: #6b7a8b;
  font-weight: 900;
  font-size: 12px;
  display: flex;
  align-items: center;
  gap: 6px;
}

.bars {
  display: inline-flex;
  gap: 2px;
  transform: translateY(1px);
}

.bar {
  width: 4px;
  border-radius: 2px;
  background: rgba(0, 0, 0, 0.18);
}

.bar.b1 { height: 6px; }
.bar.b2 { height: 9px; }
.bar.b3 { height: 12px; }
.bar.b4 { height: 15px; }

.signal.strong .bar {
  background: rgba(31, 191, 132, 0.8);
}

.signal.mid .bar {
  background: rgba(255, 193, 7, 0.75);
}

.signal.weak .bar {
  background: rgba(255, 107, 107, 0.75);
}

.actions {
  margin-top: 12px;
  display: flex;
  gap: 10px;
}

.btn {
  flex: 1;
  height: 44px;
  border-radius: 10px;
  border: 0;
  cursor: pointer;
  font-weight: 900;
  font-size: 14px;
  text-decoration: none;
  display: flex;
  align-items: center;
  justify-content: center;
}

.btn.primary {
  background: linear-gradient(180deg, #2f8dd7 0%, #2a7fc7 100%);
  color: rgba(255, 255, 255, 0.95);
}

.btn.ghost {
  background: rgba(0, 0, 0, 0.06);
  color: #1e2a36;
  border: 1px solid rgba(0, 0, 0, 0.08);
}

.modal-mask {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.45);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 18px;
  z-index: 9999;
}

.modal {
  width: min(520px, 94vw);
  background: #fff;
  color: #15212f;
  border-radius: 14px;
  box-shadow: 0 25px 70px rgba(0, 0, 0, 0.35);
  padding: 18px 18px 14px;
}

.modal h3 {
  margin: 0 0 10px;
  font-size: 16px;
  font-weight: 900;
}

.modal p {
  margin: 0 0 14px;
  color: rgba(21, 33, 47, 0.75);
  font-weight: 700;
  line-height: 1.6;
}

.modal .ok {
  width: 100%;
  height: 42px;
  border-radius: 10px;
  border: 0;
  cursor: pointer;
  font-weight: 900;
  background: linear-gradient(180deg, #2f8dd7 0%, #2a7fc7 100%);
  color: rgba(255, 255, 255, 0.95);
}

@media (max-width: 1020px) {
  .grid {
    grid-template-columns: repeat(2, 1fr);
  }
  .stats {
    grid-template-columns: repeat(2, 1fr);
  }
}

@media (max-width: 620px) {
  .netsms-page {
    padding-bottom: env(safe-area-inset-bottom);
  }
  .wrap {
    padding: 14px 12px 36px;
  }
  .h1 {
    font-size: 28px;
  }
  .sub {
    font-size: 13px;
  }
  .notice {
    padding: 14px;
  }
  .notice-title {
    font-size: 17px;
  }
  .grid {
    grid-template-columns: 1fr;
  }
  .phone {
    font-size: 22px;
  }
  .stat .num {
    font-size: 28px;
  }
  .search,
  .select {
    width: 100%;
    min-width: 0;
    font-size: 16px;
  }
  .filters {
    align-items: stretch;
    padding: 10px;
  }
  .f-left,
  .f-right {
    width: 100%;
    justify-content: flex-start;
  }
  .chip {
    height: 34px;
    padding: 0 12px;
    font-size: 13px;
  }
  .actions {
    flex-direction: column;
  }
  .btn {
    width: 100%;
  }
}
</style>
